/**
 * <suay-fit-finder> — Suay Fit Finder quiz.
 * Section: sections/suay-fit-finder.liquid
 * Visual spec: suay-prototype/prototypes/fit-finder/
 *
 * Progressive enhancement: the section renders a static silhouette
 * comparison grid by default ([data-fit-finder-fallback]). When this
 * component connects, it hides the fallback and reveals the quiz
 * ([data-fit-finder-quiz]). Without JS, shoppers still get a useful
 * silhouette comparison.
 *
 * ANALYTICS / ZERO-PARTY DATA HOOK
 * On completion the component dispatches a bubbling, composed CustomEvent:
 *
 *   suay:fit-finder:result
 *   detail: {
 *     sectionId:  string,                  // Shopify section id
 *     silhouette: 'apex'|'endurance'|'drift'|'nomad'|'horizon',
 *     runnerUp:   string|null,             // second-ranked silhouette key (same exact-first
 *                                          // ranking as the winner, see SuayFitFinder.rank)
 *     answers: {
 *       structure: 'structured'|'between'|'soft',
 *       crown:     'full'|'balanced'|'low',
 *       brim:      'curved'|'flat',
 *       context:   'everyday'|'active'|'travel'|'statement'
 *     },
 *     size: null | {                       // null when skipped/step disabled
 *       band: 'S/M'|'L/XL'|'XL+'|null,     // null when the measurement was out of range
 *       source: 'self-report'|'measure',
 *       circumference: number|null,        // raw value when measured
 *       unit: 'in'|'cm',
 *       outOfRange: boolean,               // true: measurement outside the published bands (high fit risk)
 *       availableInResult: boolean|null    // false when the winning silhouette isn't offered in this band
 *     },
 *     scores: { [silhouetteKey]: number }  // points totals for eligible silhouettes; ranking
 *                                          // is exact-match-first, so the winner is not
 *                                          // always the top scorer
 *   }
 *
 * Listen with:
 *   document.addEventListener('suay:fit-finder:result', (e) => { ... });
 * (Klaviyo/analytics capture can subscribe without touching this file.)
 */

class SuayFitFinder extends HTMLElement {
  /**
   * Scoring model.
   * Q1 (structure) defines the eligible silhouette set + base points;
   * Q2–Q4 add points. Among eligible silhouettes, ranking is exact-first:
   * fewest missed physical answers (structure, crown, brim — see
   * ATTRIBUTES) wins, then the highest points total, then tie-break order
   * apex > endurance > drift > nomad > horizon (flagship first).
   * Silhouettes without a configured block are excluded from eligibility.
   *
   * Crown: Apex sits slightly deeper than Endurance (owner ruling, theme
   * e8ceb7b; PRODUCT-TRUTHS.md in the web-mono repo), so "full coverage"
   * points go to Apex and Horizon and "balanced" points go to Endurance
   * and Drift.
   */
  static ELIGIBLE = {
    structured: ['apex', 'endurance', 'horizon'],
    between: ['drift', 'apex', 'nomad'],
    soft: ['nomad', 'drift']
  };

  static SCORES = {
    structure: {
      structured: { apex: 3, endurance: 3, horizon: 3 },
      between: { drift: 3, apex: 1, nomad: 1 },
      soft: { nomad: 3, drift: 1 }
    },
    crown: {
      full: { apex: 2, horizon: 2 },
      balanced: { endurance: 2, drift: 2 },
      low: { nomad: 2, drift: 1 }
    },
    brim: {
      curved: { apex: 2, endurance: 2, nomad: 2 },
      flat: { drift: 2, horizon: 2 }
    },
    context: {
      everyday: { apex: 2, nomad: 1 },
      active: { endurance: 2, apex: 1 },
      travel: { nomad: 2, drift: 1 },
      statement: { horizon: 2, drift: 1 }
    }
  };

  static TIE_ORDER = ['apex', 'endurance', 'drift', 'nomad', 'horizon'];

  /**
   * How each silhouette is actually built, answered in the quiz's own terms
   * (docs/content-taxonomy-qa.md; web-mono PRODUCT-TRUTHS.md). SCORES carries
   * one-point affinities (Apex scores 1 on "in between") that only nudge
   * the ranking, so whether a hat matches an answer is decided here, never
   * by "scored above zero".
   */
  static ATTRIBUTES = {
    apex: { structure: 'structured', crown: 'full', brim: 'curved' },
    endurance: { structure: 'structured', crown: 'balanced', brim: 'curved' },
    drift: { structure: 'between', crown: 'balanced', brim: 'flat' },
    nomad: { structure: 'soft', crown: 'low', brim: 'curved' },
    horizon: { structure: 'structured', crown: 'full', brim: 'flat' }
  };

  /* Physical questions decide exactness. Context (everyday / active /
     travel / statement) is a preference nudge in SCORES, not a construction
     fact, so it never demotes a hat that is built the way the shopper asked. */
  static EXACT_QUESTIONS = ['structure', 'crown', 'brim'];

  static ANSWER_PHRASES = {
    structure: { structured: 'structured shape', between: 'in-between structure', soft: 'broken-in soft' },
    crown: { full: 'full coverage', balanced: 'balanced crown', low: 'low profile' },
    brim: { curved: 'curved brim', flat: 'flat brim' },
    context: { everyday: 'everyday wear', active: 'built for motion', travel: 'packs for travel', statement: 'out front' }
  };

  /** Size bands from the Suay size guide. */
  static SIZE_BANDS = {
    sm: { label: 'S/M', note: 'S/M fits 20⅞″–22¼″ (53–56.5 cm) — smaller to average heads.' },
    lxl: { label: 'L/XL', note: 'L/XL fits 22⅜″–23½″ (56.8–59.7 cm) — our most popular size.' },
    xlp: { label: 'XL+', note: 'XL+ fits 23⅝″–25″ (60–63.5 cm) — built for bigger heads, extra room without maxing out the snaps.' }
  };

  #config = null;
  #steps = [];
  #current = 'intro';
  #interacted = false;
  /* `measure` is the measurement as the shopper actually entered it, with the
     unit it was entered in. The unit toggle rewrites the input for display
     only, and rounding that display to the nearest 1/8in can cross a band
     edge — 56.7 cm is S/M but displays as 22.375in, which reads L/XL. Sizing
     from the canonical entry keeps a display change from resizing anyone. */
  #state = { answers: {}, size: null, sizeSource: null, sizeOutOfRange: false, circumference: null, unit: 'in', measure: null };

  connectedCallback() {
    const configEl = this.querySelector('[data-fit-finder-config]');
    const quiz = this.querySelector('[data-fit-finder-quiz]');
    const fallback = this.querySelector('[data-fit-finder-fallback]');
    if (!configEl || !quiz) return;

    try {
      this.#config = JSON.parse(configEl.textContent);
    } catch (error) {
      console.error('[suay-fit-finder] invalid config JSON', error);
      return;
    }
    if (!this.#config.silhouettes || Object.keys(this.#config.silhouettes).length === 0) return;

    /* Upgrade: swap static comparison for the interactive quiz. */
    if (fallback) fallback.hidden = true;
    quiz.hidden = false;

    this.#steps = Array.from(quiz.querySelectorAll('[data-screen]')).map(
      (screen) => screen.getAttribute('data-screen')
    );

    this.addEventListener('click', this.#handleClick.bind(this));
    /* Capture the measurement in the unit it was typed in, so a later unit
       toggle — which rounds for display — cannot reclassify it. */
    this.addEventListener('input', (event) => {
      if (!event.target.closest('[data-measure-input]')) return;
      const value = Number.parseFloat(event.target.value);
      this.#state.measure = Number.isNaN(value) ? null : { value, unit: this.#state.unit };
    });
  }

  #handleClick(event) {
    if (event.target.closest('[data-start]')) {
      this.#interacted = true;
      this.#show(this.#steps[1]);
      return;
    }

    if (event.target.closest('[data-back]')) {
      const index = this.#steps.indexOf(this.#current);
      this.#show(this.#steps[Math.max(0, index - 1)]);
      return;
    }

    if (event.target.closest('[data-restart]')) {
      this.#state = { answers: {}, size: null, sizeSource: null, sizeOutOfRange: false, circumference: null, unit: this.#state.unit, measure: null };
      const measureInput = this.querySelector('[data-measure-input]');
      if (measureInput) measureInput.value = '';
      this.#show('intro');
      return;
    }

    const option = event.target.closest('[data-answer]');
    if (option) {
      const question = option.closest('[data-question]')?.getAttribute('data-question');
      if (!question) return;
      this.#state.answers[question] = option.getAttribute('data-answer');
      this.#next();
      return;
    }

    const pill = event.target.closest('[data-size]');
    if (pill) {
      const band = pill.getAttribute('data-size');
      this.#state.size = band === 'skip' ? null : band;
      this.#state.sizeSource = band === 'skip' ? null : 'self-report';
      this.#state.sizeOutOfRange = false;
      this.#state.circumference = null;
      this.#state.measure = null;
      this.#renderResult();
      return;
    }

    const unit = event.target.closest('[data-unit]');
    if (unit) {
      const newUnit = unit.getAttribute('data-unit');
      const input = this.querySelector('[data-measure-input]');
      if (input && newUnit !== this.#state.unit) {
        /* Convert any typed value so 22 in doesn't get re-read as 22 cm. */
        const typed = Number.parseFloat(input.value);
        if (!Number.isNaN(typed)) {
          input.value = newUnit === 'cm'
            ? String(Math.round(typed * 2.54 * 10) / 10)
            : String(Math.round((typed / 2.54) * 8) / 8);
        }
        input.step = newUnit === 'cm' ? '0.1' : '0.125';
        input.placeholder = newUnit === 'cm' ? '56' : '22';
      }
      this.#state.unit = newUnit;
      for (const button of this.querySelectorAll('[data-unit]')) {
        button.setAttribute('aria-pressed', String(button === unit));
      }
      return;
    }

    if (event.target.closest('[data-measure-submit]')) {
      const input = this.querySelector('[data-measure-input]');
      /* Size from what was typed, not from the rounded display value. Falls
         back to reading the field for input the listener never saw (autofill,
         a programmatic set, or a browser that skipped the event). */
      const typed = Number.parseFloat(input?.value ?? '');
      const measure = this.#state.measure
        ?? (Number.isNaN(typed) ? null : { value: typed, unit: this.#state.unit });
      const band = measure ? this.#sizeFromCircumference(measure.value, measure.unit) : null;
      this.#state.measure = measure;
      this.#state.sizeOutOfRange = band === 'out';
      this.#state.size = band === 'out' ? null : band;
      this.#state.sizeSource = this.#state.size ? 'measure' : null;
      this.#state.circumference = band && measure ? measure.value : null;
      this.#renderResult();
    }
  }

  #next() {
    const next = this.#steps[this.#steps.indexOf(this.#current) + 1];
    if (next === 'result') {
      this.#renderResult();
      return;
    }
    this.#show(next);
  }

  #show(step) {
    this.#current = step;
    for (const screen of this.querySelectorAll('[data-screen]')) {
      screen.hidden = screen.getAttribute('data-screen') !== step;
    }

    const progress = this.querySelector('[data-progress]');
    if (progress) {
      const progressSteps = this.#steps.filter((name) => name !== 'intro' && name !== 'result');
      const index = progressSteps.indexOf(step);
      progress.hidden = index === -1;
      if (index !== -1) {
        const label = this.querySelector('[data-progress-label]');
        const track = this.querySelector('[data-progress-track]');
        const bar = this.querySelector('[data-progress-bar]');
        if (label) label.textContent = `Step ${index + 1} of ${progressSteps.length}`;
        if (track) {
          track.setAttribute('aria-valuemax', String(progressSteps.length));
          track.setAttribute('aria-valuenow', String(index + 1));
        }
        if (bar) bar.style.width = `${((index + 1) / progressSteps.length) * 100}%`;
      }
    }

    if (!this.#interacted) return;
    const heading = this.querySelector(`[data-screen="${step}"] .suay-fit-finder__heading`);
    heading?.focus();
  }

  /**
   * Returns a band key, 'out' for a real measurement outside the published
   * bands, or null for empty/invalid input. The cm path uses the size
   * guide's published cm bands directly — they're rounded, so converting
   * to inches misclassifies the cm edges (e.g. 53 cm is a published S/M
   * min but converts to 20.87″). Between-band gap values take the SMALLER
   * band, per the size guide's fit tip: "If you're between sizes, go with
   * the smaller one — it'll adjust to you."
   */
  #sizeFromCircumference(value, unit) {
    if (!value || Number.isNaN(value)) return null;
    if (unit === 'cm') {
      if (value < 53 || value > 63.5) return 'out';
      if (value < 56.8) return 'sm';
      if (value < 60) return 'lxl';
      return 'xlp';
    }
    if (value < 20.875 || value > 25) return 'out';
    if (value < 22.375) return 'sm';
    if (value < 23.625) return 'lxl';
    return 'xlp';
  }

  #recommend() {
    return SuayFitFinder.rank(this.#state.answers, Object.keys(this.#config.silhouettes));
  }

  /**
   * Pure ranking over the scoring model (no DOM, unit-tested in
   * tests/fit-finder-scoring.unit.mjs). `available` is the silhouette keys
   * that have a configured block. Ranks on how many physical answers a hat
   * misses first, so a context or tie-order nudge can never beat a hat that
   * is built exactly as asked; points and TIE_ORDER only order hats that
   * miss the same number of answers.
   */
  static rank(answers, available) {
    let eligible = (SuayFitFinder.ELIGIBLE[answers.structure] || SuayFitFinder.TIE_ORDER)
      .filter((key) => available.includes(key));
    if (eligible.length === 0) eligible = [...available];

    const totals = {};
    const missed = {};
    for (const key of eligible) {
      totals[key] = 0;
      missed[key] = SuayFitFinder.EXACT_QUESTIONS.filter(
        (question) => answers[question] && SuayFitFinder.ATTRIBUTES[key]?.[question] !== answers[question]
      ).length;
    }
    for (const [question, options] of Object.entries(SuayFitFinder.SCORES)) {
      const points = options[answers[question]] || {};
      for (const [key, value] of Object.entries(points)) {
        if (key in totals) totals[key] += value;
      }
    }

    const ranked = [...eligible].sort((a, b) => {
      if (missed[a] !== missed[b]) return missed[a] - missed[b];
      if (totals[b] !== totals[a]) return totals[b] - totals[a];
      return SuayFitFinder.TIE_ORDER.indexOf(a) - SuayFitFinder.TIE_ORDER.indexOf(b);
    });

    return { winner: ranked[0], runnerUp: ranked[1] || null, scores: totals };
  }

  #renderResult() {
    const recommendation = this.#recommend();
    const silhouette = this.#config.silhouettes[recommendation.winner];

    const media = this.querySelector('[data-result-media]');
    const image = this.querySelector('[data-result-image]');
    if (image && silhouette.image) {
      image.src = silhouette.image;
      image.alt = silhouette.alt || silhouette.name;
      if (media) media.hidden = false;
    } else if (media) {
      media.hidden = true;
    }

    const name = this.querySelector('[data-result-name]');
    if (name) name.textContent = silhouette.name;

    const why = this.querySelector('[data-result-why]');
    if (why) why.textContent = silhouette.why;

    const band = this.#state.size ? SuayFitFinder.SIZE_BANDS[this.#state.size] : null;
    /* A silhouette block without a sizes array is treated as offered in
       every band (older saved sections predate the offers_xlp setting). */
    const sizeAvailable = !band
      || !Array.isArray(silhouette.sizes)
      || silhouette.sizes.includes(this.#state.size);
    const altKey = sizeAvailable
      ? null
      : [recommendation.runnerUp, ...SuayFitFinder.TIE_ORDER].find(
          (key) => key
            && key !== recommendation.winner
            && this.#config.silhouettes[key]
            && (!Array.isArray(this.#config.silhouettes[key].sizes) || this.#config.silhouettes[key].sizes.includes(this.#state.size))
        ) || null;

    /* Don't dead-end shoppers into a collection that lacks their size —
       the primary action points at the closest silhouette that has it. */
    const ctaSilhouette = altKey ? this.#config.silhouettes[altKey] : silhouette;
    const cta = this.querySelector('[data-result-cta]');
    if (cta) {
      if (ctaSilhouette.url) {
        cta.href = ctaSilhouette.url;
        cta.textContent = ctaSilhouette.cta || `Shop ${ctaSilhouette.name}`;
        cta.hidden = false;
      } else {
        cta.hidden = true;
      }
    }

    const spec = this.querySelector('[data-result-spec]');
    if (spec) {
      const phrases = [];
      for (const [question, labels] of Object.entries(SuayFitFinder.ANSWER_PHRASES)) {
        const answer = this.#state.answers[question];
        if (answer && labels[answer]) phrases.push(labels[answer]);
      }
      spec.textContent = `${silhouette.spec} · You asked for: ${phrases.join(' · ')}`;
    }

    const sizeEl = this.querySelector('[data-result-size]');
    if (sizeEl) {
      sizeEl.textContent = '';
      const appendSizeGuideLink = () => {
        if (!this.#config.sizeGuideUrl) return;
        const link = document.createElement('a');
        link.href = this.#config.sizeGuideUrl;
        link.textContent = 'See the size guide';
        sizeEl.append(' ', link, '.');
      };
      if (this.#state.sizeOutOfRange) {
        sizeEl.append('Your measurement falls outside our published size range (20⅞–25″ / 53–63.5 cm). Double-check the tape sits just above your ears and eyebrows.');
        appendSizeGuideLink();
      } else if (band && !sizeAvailable) {
        /* Winner isn't offered in this band (e.g. Nomad has no XL+) —
           be honest and point at the closest silhouette that is. */
        const offered = (silhouette.sizes || [])
          .map((key) => SuayFitFinder.SIZE_BANDS[key]?.label)
          .filter(Boolean)
          .join(' and ');
        const strong = document.createElement('strong');
        strong.textContent = band.label;
        sizeEl.append('Heads up: your size is ', strong, `, and ${silhouette.name.replace(/^The /, 'the ')} comes in ${offered} only.`);
        if (altKey) {
          const alt = this.#config.silhouettes[altKey];
          sizeEl.append(` The closest fit in ${band.label} is `);
          if (alt.url) {
            const altLink = document.createElement('a');
            altLink.href = alt.url;
            altLink.textContent = alt.name;
            sizeEl.append(altLink);
          } else {
            sizeEl.append(alt.name);
          }
          sizeEl.append(' — shop it above.');
        }
        appendSizeGuideLink();
      } else if (band) {
        const prefix = this.#state.sizeSource === 'measure'
          ? 'Based on your measurement, your size is '
          : 'Your size: ';
        const strong = document.createElement('strong');
        strong.textContent = band.label;
        sizeEl.append(prefix, strong, `. ${band.note}`);
      } else {
        sizeEl.append('Not sure on size? Most adults measure 21–24″ (53–61 cm).');
        appendSizeGuideLink();
      }
    }

    const runnerEl = this.querySelector('[data-result-runner-up]');
    if (runnerEl) {
      runnerEl.textContent = '';
      const runnerKey = recommendation.runnerUp;
      if (runnerKey && recommendation.scores[runnerKey] > 0) {
        const runner = this.#config.silhouettes[runnerKey];
        runnerEl.append('Close second: ');
        if (runner.url) {
          const link = document.createElement('a');
          link.href = runner.url;
          link.textContent = runner.name;
          runnerEl.append(link);
        } else {
          runnerEl.append(runner.name);
        }
        runnerEl.append(` — ${runner.spec.toLowerCase()}.`);
      }
    }

    this.dispatchEvent(new CustomEvent('suay:fit-finder:result', {
      bubbles: true,
      composed: true,
      detail: {
        sectionId: this.dataset.sectionId || null,
        silhouette: recommendation.winner,
        runnerUp: recommendation.runnerUp,
        answers: { ...this.#state.answers },
        size: band
          ? {
              band: band.label,
              source: this.#state.sizeSource,
              circumference: this.#state.circumference,
              unit: this.#state.measure?.unit ?? this.#state.unit,
              outOfRange: false,
              availableInResult: sizeAvailable
            }
          : this.#state.sizeOutOfRange
            /* Out-of-range shoppers are the highest fit-risk segment —
               keep them distinguishable from "Not sure" skips. */
            ? {
                band: null,
                source: 'measure',
                circumference: this.#state.circumference,
                unit: this.#state.measure?.unit ?? this.#state.unit,
                outOfRange: true,
                availableInResult: null
              }
            : null,
        scores: recommendation.scores
      }
    }));

    this.#show('result');
  }
}

if (!customElements.get('suay-fit-finder')) {
  customElements.define('suay-fit-finder', SuayFitFinder);
}
